import React, { CSSProperties, useContext, useEffect, useRef } from 'react';
import { Paper } from '@mui/material';
import { SMALL_CARD_HEIGHT, SMALL_CARD_WIDTH } from 'constants/gameConstants';
import ScaleContext from 'contexts/ScaleContext';
import gsap from 'gsap';

import {
  CARD_BACK_ALT,
  CARD_BACK_SRC,
  cardAltText,
  cardCornerRadiusForWidth,
  cardFaceSrc
} from './deck';
import { Card } from '.';

type CardPropType = Card & {
  isFaceDown?: boolean;
  elevation?: number;
  enableScale?: boolean;
  /** Dim the card front (used at showdown for non-winning hole cards). */
  isDimmed?: boolean;
};

/**
 * Card flip animation matching Unity UICard.FlipCardFaceUp():
 *   Phase 1: back scaleX 1→0 (0.2s)
 *   Phase 2: front scaleX 0→1 (0.2s)
 * Total: 0.4s two-phase flip
 *
 * Face and back are single artworks from the deck (see ./deck.ts); this
 * component only sizes, flips and dims them.
 */
const SmallCard = ({
  suit,
  rank,
  isFaceDown = false,
  enableScale = false,
  isDimmed = false
}: CardPropType) => {
  const { scale: globalScale } = useContext(ScaleContext);
  const scale = enableScale ? globalScale : 1;
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const prevFaceDown = useRef(isFaceDown);

  useEffect(() => {
    const front = frontRef.current;
    const back = backRef.current;
    if (!front || !back) return;

    if (prevFaceDown.current === isFaceDown) {
      // Initial render — set correct state without animation
      gsap.set(front, { scaleX: isFaceDown ? 0 : 1 });
      gsap.set(back, { scaleX: isFaceDown ? 1 : 0 });
      return;
    }

    prevFaceDown.current = isFaceDown;

    // Animate flip
    const tl = gsap.timeline();
    if (isFaceDown) {
      // Flip to back: front shrinks, back grows
      tl.to(front, { scaleX: 0, duration: 0.2, ease: 'power2.in' }).to(back, {
        scaleX: 1,
        duration: 0.2,
        ease: 'power2.out'
      });
    } else {
      // Flip to front: back shrinks, front grows
      tl.to(back, { scaleX: 0, duration: 0.2, ease: 'power2.in' }).to(front, {
        scaleX: 1,
        duration: 0.2,
        ease: 'power2.out'
      });
    }

    return () => {
      tl.kill();
    };
  }, [isFaceDown]);

  const face = cardFaceSrc(rank, suit);
  // The artwork's own corners, at this card's width — so the shadow follows them.
  const cornerRadius = cardCornerRadiusForWidth(SMALL_CARD_WIDTH * scale);

  const BaseStyle: CSSProperties = {
    position: 'relative',
    width: SMALL_CARD_WIDTH * scale,
    height: SMALL_CARD_HEIGHT * scale,
    // Apply dimming to the wrapper so it survives GSAP's transform updates on
    // the inner Paper. Filter inherits visually through both faces of the card.
    ...(isDimmed && { filter: 'brightness(0.4)' })
  };

  return (
    <div style={BaseStyle}>
      {face && (
        <Paper
          ref={frontRef}
          elevation={0}
          style={{
            ...SideStyle,
            borderRadius: cornerRadius,
            transform: `scaleX(${isFaceDown ? 0 : 1})`
          }}>
          <img src={face} alt={cardAltText(rank, suit)} draggable={false} style={ArtStyle} />
        </Paper>
      )}
      <Paper
        ref={backRef}
        elevation={0}
        style={{
          ...SideStyle,
          borderRadius: cornerRadius,
          transform: `scaleX(${isFaceDown ? 1 : 0})`
        }}>
        <img src={CARD_BACK_SRC} alt={CARD_BACK_ALT} draggable={false} style={ArtStyle} />
      </Paper>
    </div>
  );
};

export default SmallCard;

// One side of the card — the artwork paints the face (or back) including its
// white fill and rounded corners; the box only carries the shadow.
const SideStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: '100%',
  background: 'transparent',
  boxShadow: '0 1px 2px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.08)'
};

const ArtStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  userSelect: 'none'
};
